// src/services/teacherService.js
import api from './api';
// import socket from './socket';
import socketService from './socket';

// ============ HELPER FUNCTIONS ============

const calculateEndTime = (date, time, durationMinutes) => {
  try {
    const [hours, minutes] = time.split(':').map(Number);
    const startDateTime = new Date(date);
    startDateTime.setHours(hours, minutes, 0);
    
    const endDateTime = new Date(startDateTime.getTime() + durationMinutes * 60000);
    
    const endHours = endDateTime.getHours().toString().padStart(2, '0');
    const endMinutes = endDateTime.getMinutes().toString().padStart(2, '0');
    
    return `${endHours}:${endMinutes}`;
  } catch (e) {
    return null;
  }
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'Recent';
  try {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return 'Recent';
  }
};

const isWithinJoinWindow = (date, time) => {
  if (!date || !time) return false;
  try {
    const sessionDateTime = new Date(`${date}T${time}:00`);
    const now = new Date();
    const diffMinutes = (sessionDateTime - now) / (1000 * 60);
    return diffMinutes <= 15 && diffMinutes > -120;
  } catch (e) {
    return false;
  }
};

// ============ TEACHER PROFILE MANAGEMENT ============
export const fetchTeacherProfile = async (teacherId) => {
  try {
    console.log("🔥 ACCEPT FUNCTION RUNNING");
    const response = await api.get(`/teachers/${teacherId}/profile`);

    console.log("👤 RAW PROFILE RESPONSE:", response.data);

    // ✅ FIX: unwrap profile safely
    return response.data?.profile || response.data;

  } catch (error) {
    console.error('Error fetching teacher profile:', error);
    return null;
  }
};

export const updateTeacherProfile = async (teacherId, profileData) => {
  try {
    const response = await api.put(`/teachers/${teacherId}/profile`, profileData);
    return response.data;
  } catch (error) {
    console.error('Error updating teacher profile:', error);
    throw error;
  }
};

export const sendHeartbeat = async (sessionId) => {
  return api.post(`/sessions/${sessionId}/heartbeat`);
};

export const updateTeacherAvailability = async (teacherId, availability) => {
  try {
    const response = await api.put(`/teachers/${teacherId}/availability`, { availability });
    return response.data;
  } catch (error) {
    console.error('Error updating availability:', error);
    throw error;
  }
};


// ============ SESSION REQUESTS ============
export const fetchSessionRequests = async (teacherId) => {
  try {
    console.log('📋 Fetching session requests for teacher:', teacherId);
    
    const response = await api.get(`/teachers/${teacherId}/session-requests`);

    const requests = (response.data.requests || response.data || []).map(req => ({
      ...req,
      requestedAt: req.requestedAt ? new Date(req.requestedAt) : new Date()
    }));

    console.log(`✅ Found ${requests.length} pending requests`);
    return requests;

  } catch (error) {
    console.error('❌ Error fetching session requests:', error);
    return [];
  }
};

// ============ TEACHER SESSIONS ============
export const fetchTeacherSessions = async (teacherId) => {
  try {
    console.log('📋 Fetching all sessions for teacher:', teacherId);
    
    const response = await api.get(`/teachers/${teacherId}/sessions`);

    const rawData = response.data;

    console.log("🔥 RAW BACKEND RESPONSE:", rawData);

    // ✅ USE BACKEND STRUCTURE DIRECTLY
    const scheduled = rawData.scheduled || [];
    const ongoing = rawData.ongoing || [];
    const completed = rawData.completed || [];
    const all = rawData.all || [];

    console.log(`✅ Found ${all.length} total sessions`);

    return {
      scheduled,
      ongoing,
      completed,
      all
    };

  } catch (error) {
    console.error('❌ Error fetching teacher sessions:', error);
    return { scheduled: [], ongoing: [], completed: [], all: [] };
  }
};
// ============ SCHEDULE SESSION REQUEST ============
export const scheduleSessionRequest = async (requestId, teacherId, teacherName, scheduleData) => {
  console.log('📅 Scheduling session from request:', requestId);
  return acceptSessionRequest(requestId, teacherId, teacherName, scheduleData);
};

// ============ HIRING REQUESTS MANAGEMENT ============
export const fetchHiringRequests = async (teacherId) => {
  try {
    console.log('📋 Fetching hiring requests for teacher:', teacherId);

    const response = await api.get(`/teachers/${teacherId}/hiring-requests`);

    const rawData = response.data;

    const requests = Array.isArray(rawData)
      ? rawData
      : Array.isArray(rawData.requests)
        ? rawData.requests
        : Array.isArray(rawData.data)
          ? rawData.data
          : [];

    console.log(`✅ Found ${requests.length} hiring requests`);

    // ✅ FIX: return object
    return requests;

  } catch (error) {
    console.error('❌ Error fetching hiring requests:', error);
    return [];
  }
};

export const acceptHiringRequest = async (requestId, scheduleData, teacherId, teacherName) => {
  try {
    // ✅ REMOVED — frontend no longer generates meeting links
    // The backend generates a real Jitsi link and saves it to Firestore
    // We just send the schedule data and use whatever the backend returns
 
    const response = await api.put(
      `/teachers/hiring-requests/${requestId}/accept`,
      {
        scheduleDate: scheduleData.scheduleDate,
        scheduleTime: scheduleData.scheduleTime,
        duration: scheduleData.duration,
        teacherName
      }
    );
 
    // ✅ Use the meeting link from the backend response
    const meetingLink = response.data.meetingLink;
 
    // Emit real-time event with the real link
    socketService.emit('hiring-request-accepted', {
      requestId,
      teacherId,
      teacherName,
      meetingLink,
      ...response.data
    });
 
    return response.data;
 
  } catch (error) {
    console.error('Error accepting hiring request:', error);
    throw error;
  }
};
 

export const rejectHiringRequest = async (requestId, teacherId) => {
  try {
    const response = await api.post(`/teachers/${teacherId}/hiring-requests/${requestId}/reject`);
    
    // Emit real-time event
    socketService.emit('hiring-request-rejected', {
      requestId,
      teacherId
    });
    
    return response.data;
  } catch (error) {
    console.error('Error rejecting hiring request:', error);
    throw error;
  }
};

// ============ SESSION REQUESTS MANAGEMENT ============
export const subscribeToTeacherRequests = (teacherId, callback) => {
  console.log('🔌 Setting up real-time listener for teacher:', teacherId);
  
  const eventName = `teacher-requests:${teacherId}`;
  
  socketService.on(eventName, (data) => {
    console.log('📡 Real-time teacher requests update:', data);
    const requests = data.requests.map(req => ({
      ...req,
      requestedAt: req.requestedAt ? new Date(req.requestedAt) : new Date(),
      formattedDate: formatDate(req.requestedAt)
    }));
    callback(requests);
  });
  
  // Initial fetch
  fetchSessionRequests(teacherId).then(requests => {
    callback(requests);
  });
  
  return () => {
    console.log('🔌 Cleaning up teacher requests listener');
    socketService.off(eventName);
  };
};

export const acceptSessionRequest = async (requestId, teacherId, teacherName, scheduleData) => {
  try {
    console.log('✅ ACCEPTING SESSION REQUEST:', requestId);
 
    if (!scheduleData.scheduleDate || !scheduleData.scheduleTime || !scheduleData.duration) {
      throw new Error('Please provide date, time, and duration');
    }
 
    // ✅ REMOVED — frontend no longer generates meeting links
    // The backend generates the Jitsi link in a two-step process
    // and returns it in the response
 
    const endTime = calculateEndTime(
      scheduleData.scheduleDate,
      scheduleData.scheduleTime,
      parseInt(scheduleData.duration) || 60
    );
 
    const response = await api.post(
      `/teachers/${teacherId}/session-requests/${requestId}/accept`,
      {
        scheduleDate: scheduleData.scheduleDate,
        scheduleTime: scheduleData.scheduleTime,
        duration: scheduleData.duration,
        endTime,
        teacherName
      }
    );
 
    // ✅ Use the meeting link from the backend response
    const meetingLink = response.data.meetingLink;
 
    socketService.emit('session-request-accepted', {
      requestId,
      teacherId,
      teacherName,
      sessionId: response.data.sessionId,
      meetingLink,
      scheduleData
    });
 
    return {
      success: true,
      sessionId: response.data.sessionId,
      meetingLink,
      message: `Session scheduled for ${scheduleData.scheduleDate} at ${scheduleData.scheduleTime}`
    };
 
  } catch (error) {
    console.error('❌ ERROR in acceptSessionRequest:', error);
    throw new Error(
      error.response?.data?.message ||
      error.message ||
      'Failed to accept session request'
    );
  }
};

export const rejectSessionRequest = async (requestId, teacherId, reason = '') => {
  try {
    console.log('❌ Rejecting session request:', requestId);
    
    const response = await api.post(`/teachers/${teacherId}/session-requests/${requestId}/reject`, {
      reason
    });
    
    // Emit real-time event
    socketService.emit('session-request-rejected', {
      requestId,
      teacherId,
      reason
    });
    
    return response.data;
    
  } catch (error) {
    console.error('❌ Error rejecting request:', error);
    throw error;
  }
};

export const subscribeToTeacherSessions = (teacherId, callback) => {
  console.log('🔌 Setting up real-time listener for teacher sessions:', teacherId);
  
  const eventName = `teacher-sessions:${teacherId}`;
  
  socketService.on(eventName, (data) => {
    console.log('📡 Real-time teacher sessions update:', data);
    const sessions = data.sessions.map(session => ({
      ...session,
      scheduleDateObj: session.scheduleDate ? new Date(session.scheduleDate) : null,
      canStart: session.status === 'scheduled' && 
                isWithinJoinWindow(session.scheduleDate, session.scheduleTime)
    }));
    callback(sessions);
  });
  
  // Initial fetch
  fetchTeacherSessions(teacherId).then(result => {
    callback(result.all);
  });
  
  return () => {
    console.log('🔌 Cleaning up teacher sessions listener');
    socketService.off(eventName);
  };
};

export const startSession = async (sessionId, teacherId) => {
  try {
    if (process.env.NODE_ENV === "development") {
      console.log('▶️ Starting session:', sessionId);
    }

    // ✅ FIXED — URL now matches the route defined in teacherRoutes.js
    // Changed from POST /sessions/:id/start
    // to PUT /teachers/:teacherId/sessions/:id/start
    const response = await api.put(
      `/teachers/${teacherId}/sessions/${sessionId}/start`,
      {
        startedAt: new Date().toISOString()
      }
    );

    // Emit real-time event
    socketService.emit('session-started', {
      sessionId,
      teacherId,
      startedAt: new Date().toISOString(),
      ...response.data
    });

    return response.data;

  } catch (error) {
    console.error('❌ Error starting session:', error);
    throw error;
  }
};

export const endSession = async (sessionId, teacherId) => {
  try {
    if (process.env.NODE_ENV === "development") {
      console.log('⏹️ Ending session:', sessionId);
    }

    const endedAt = new Date().toISOString();

    // ✅ FIXED — changed POST to PUT, corrected URL path
    const response = await api.put(
      `/teachers/${teacherId}/sessions/${sessionId}/end`,
      { endedAt }
    );

    socketService.emit('session-ended', {
      sessionId,
      teacherId,
      endedAt,
      ...response.data
    });

    return response.data;

  } catch (error) {
    console.error('❌ Error ending session:', error);
    throw error;
  }
};

export const acceptInstantHelp = async (helpRequestId) => {
  const response = await api.post(
    `/teachers/help-requests/${helpRequestId}/accept`
  );

  return response.data;
};
// ============ SESSION VIDEO UPLOAD ============
export const uploadSessionVideo = async (sessionId, teacherId, videoFile, sessionNotes) => {
  try {
    if (!videoFile) {
      throw new Error('Please select a video file');
    }
    
    // Create form data for file upload
    const formData = new FormData();
    formData.append('video', videoFile);
    formData.append('sessionId', sessionId);
    formData.append('teacherId', teacherId);
    formData.append('sessionNotes', sessionNotes || '');
    
    const response = await api.post('/sessions/upload-video', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error uploading video:', error);
    throw error;
  }
};

export const completeSessionWithVideo = async (sessionId, videoUrl, sessionNotes, teacherId) => {
  try {
    const response = await api.post(`/sessions/${sessionId}/complete-with-video`, {
      videoUrl,
      sessionNotes,
      teacherId
    });
    
    return response.data;
  } catch (error) {
    console.error('Error completing session:', error);
    throw error;
  }
};


// ============ PAYMENT MANAGEMENT ============
export const processSessionPayment = async (sessionId, teacherId, teacherName, sessionType = 'hired') => {
  try {
    const response = await api.post(`/teachers/${teacherId}/process-payment`, {
      sessionId,
      teacherName,
      sessionType
    });
    
    return response.data;
  } catch (error) {
    console.error('Error processing payment:', error);
    throw error;
  }
};

// ============ WALLET AND WITHDRAWAL MANAGEMENT ============
export const fetchTeacherWallet = async (teacherId) => {
  try {
    const response = await api.get(`/teachers/${teacherId}/wallet`);
    return response.data;
  } catch (error) {
    console.error('Error fetching wallet:', error);
    return { nairaBalance: 0, totalEarnings: 0, tokenBalance: 0, pendingBalance: 0 };
  }
};

export const fetchPaymentHistory = async (teacherId) => {
  try {
    const response = await api.get(`/teachers/${teacherId}/payment-history`);
    return response.data;
  } catch (error) {
    console.error('Error fetching payment history:', error);
    return [];
  }
};

export const requestWithdrawal = async (teacherId, withdrawalData, teacherName) => {
  try {
    const { amount, bankName, accountNumber, accountName } = withdrawalData;

    if (amount <= 0) {
      throw new Error('Please enter a valid amount');
    }

    // Get teacher wallet balance
    const wallet = await fetchTeacherWallet(teacherId);

    if (amount > wallet.nairaBalance) {
      throw new Error('Insufficient balance');
    }

    const response = await api.post(`/teachers/${teacherId}/withdrawals`, {
      amount,
      bankName,
      accountNumber,
      accountName,
      teacherName
    });

    return response.data;

  } catch (error) {
    console.error('Error requesting withdrawal:', error);
    throw error;
  }
};

// ============ MONTHLY EARNINGS CALCULATION ============
export const calculateMonthlyEarnings = async (teacherId) => {
  try {
    const response = await api.get(`/teachers/${teacherId}/monthly-earnings`);
    return response.data;
  } catch (error) {
    console.error('Error calculating earnings:', error);
    return {
      totalEarned: 0,
      volunteerSessions: 0,
      paidSessions: 0,
      volunteerEDU: 0,
      paidNaira: 0,
      pendingWithdrawals: 0,
      availableForWithdrawal: 0
    };
  }
};

// ============ ANALYTICS AND REPORTS ============
export const getTeacherAnalytics = async (teacherId) => {
  try {
    const response = await api.get(`/teachers/${teacherId}/analytics`);
    return response.data;
  } catch (error) {
    console.error('Error getting teacher analytics:', error);
    return null;
  }
};

export const submitStudentReport = async (teacherId, teacherName, studentId, studentName, reportData) => {
  try {
    const response = await api.post(`/teachers/${teacherId}/student-reports`, {
      teacherName,
      studentId,
      studentName,
      reportData
    });
    
    return response.data;
  } catch (error) {
    console.error('Error submitting student report:', error);
    throw error;
  }
};

// ============ REAL-TIME LISTENERS (WebSocket) ============
let hasInitializedRealtime = false; // 🔥 GLOBAL GUARD

export const setupRealtimeListeners = (teacherId, callbacks) => {
  const {
    onHiringRequestsUpdate,
    onSessionRequestsUpdate,
    onSessionsUpdate
  } = callbacks;

  const hiringEventName = `hiring-requests:${teacherId}`;
  const sessionRequestEventName = `session-requests:${teacherId}`;
  const sessionsEventName = `sessions:${teacherId}`;

  console.log("🔌 Setting up realtime listeners");

  // ✅ SOCKET LISTENERS (SAFE)
  socketService.on(hiringEventName, (data) => {
    console.log('📡 Real-time hiring requests update:', data);
    onHiringRequestsUpdate(data);
  });

  socketService.on(sessionRequestEventName, (data) => {
    console.log('📡 Real-time session requests update:', data);
    onSessionRequestsUpdate(
      data.requests.filter(r => r.status === 'pending')
    );
  });

  socketService.on(sessionsEventName, (data) => {
    console.log('📡 Real-time sessions update:', data);

    const sessions = data.sessions.map(session => ({
      ...session,
      scheduleDate: session.scheduleDate,
      scheduleTime: session.scheduleTime,
      meetingLink: session.meetingLink || null,
      status: session.status || 'scheduled',
      canStart:
        session.status === 'scheduled' &&
        isWithinJoinWindow(session.scheduleDate, session.scheduleTime)
    }));

    onSessionsUpdate(sessions);
  });

  // ✅ INITIAL FETCH (RUN ONLY ONCE)
  if (!hasInitializedRealtime) {
    hasInitializedRealtime = true;

    console.log("📥 Running initial fetch ONCE");

    fetchHiringRequests(teacherId).then(onHiringRequestsUpdate);
    fetchSessionRequests(teacherId).then(onSessionRequestsUpdate);
    fetchTeacherSessions(teacherId).then(result =>
      onSessionsUpdate(result.all)
    );
  }

  // ✅ CLEANUP
  return () => {
    console.log("🛑 Cleaning up listeners");

    socketService.off(hiringEventName);
    socketService.off(sessionRequestEventName);
    socketService.off(sessionsEventName);
  };
};
// ============ DEFAULT EXPORT ============
const teacherService = {
  getTeacherProfile: fetchTeacherProfile,
  fetchTeacherProfile,
  updateTeacherProfile,
  acceptInstantHelp,
  updateTeacherAvailability,
  fetchHiringRequests,
  acceptHiringRequest,
  rejectHiringRequest,
  fetchSessionRequests,
  fetchTeacherSessions,
  scheduleSessionRequest,
  acceptSessionRequest,
  rejectSessionRequest,
  subscribeToTeacherRequests,
  subscribeToTeacherSessions,
  startSession,
  endSession,
  uploadSessionVideo,
  completeSessionWithVideo,
  processSessionPayment,
  fetchTeacherWallet,
  fetchPaymentHistory,
  requestWithdrawal,
  calculateMonthlyEarnings,
  getTeacherAnalytics,
  submitStudentReport,
  setupRealtimeListeners,
  sendHeartbeat
};

export default teacherService;